/**
 * =============================================================================
 * ROASTBREW - AUTH CONTEXT
 * =============================================================================
 *
 * Shared authentication context used across all components.
 * Separated into its own file to avoid circular dependencies between
 * App.jsx and components that need auth state (AuthUI, VerifyEmailPage, etc.)
 *
 * USAGE:
 * import { useAuth } from './AuthContext';
 * const { profile, algorithm, catalog, login, logout, API_URL, api } = useAuth();
 *
 * PROVIDED VALUES (production surface):
 * - profile: SINGLE SOURCE OF TRUTH for all user data, or null when logged out:
 *     { name, email, currentTier, subscription: { status, cancelAtPeriodEnd,
 *       currentPeriodEnd, updatedAt, pendingDowngradeTier, downgradeDate },
 *       profiles: [] }
 * - algorithm: content-suggestion model (arrives combined with progress from the Content API)
 * - progressByVideo: SOLE home for per-user progress, keyed by video id:
 *     { [videoId]: { positionSeconds, durationSeconds, completed } } — merged onto the catalog at render
 * - getProgress(videoId): convenience accessor into progressByVideo (or null)
 * - reportProgress(videoId, positionSeconds, durationSeconds, played?, view?): throttled, fire-and-forget
 *     heartbeat (the frontend's only write); optimistically updates progressByVideo
 * - ensureFresh(): re-mint expiring signed CDN URLs. Cheap and safe to call from
 *     anywhere — it is gated (in-flight / 60s cooldown / staleness) and returns
 *     immediately unless the `?t=` token on the cached payloads is close to expiry.
 *
 *     WHY IT EXISTS. Every URL the Content API returns carries a prefix-scoped
 *     `?t=<prefix>.<unix expiry>.<sig>` token, and the SAME token is appended to
 *     everything under a lesson's prefix — thumbnail, preview and video alike.
 *     /video/:id is fetched at page mount so its URLs are always fresh, but the
 *     CATALOG and the ALGORITHM FEED are fetched once per session and held in
 *     state indefinitely. Their tokens die while the URLs live on, so every
 *     thumbnail 403s until a manual page refresh. ensureFresh refreshes BOTH
 *     payloads together, because one token covers both.
 *
 *     App.jsx calls it on tab-visible, route change and pointer/key input, and
 *     forces it from a global image-error backstop. Nothing is on a timer: an
 *     untouched page never fires.
 *
 *     ensureFresh({ force: true }) skips ONLY the staleness gate; the in-flight
 *     lock and the 60s cooldown still apply. Use it when you have EVIDENCE a URL
 *     is dead rather than a prediction — a 403 is fact, the staleness clock is a
 *     guess that clock skew or a Worker-side TTL change can invalidate.
 *     App.jsx's own image backstop already covers every <img> in the app via a
 *     capture-phase window listener, so you are unlikely to need this directly.
 * - catalog: full content tree from the Content API
 * - catalogLoading: boolean
 * - token: JWT string | null
 * - isLoading: boolean (true during initial session validation)
 * - sessionId: string | null
 * - login / loginWithResponse / logout / refreshAccount / syncSubscription
 * - API_URL: string (backend base URL)
 * - api: { fetch, refreshToken } (authenticated API client with auto-refresh)
 *
 * TEMP COMPATIBILITY SCAFFOLD (derived from `profile`; removed once every
 * consumer reads `profile` directly — do not build new code against these):
 * - user, currentTier, subscriptionStatus, cancelAtPeriodEnd, currentPeriodEnd,
 *   pendingDowngradeTier, downgradeDate
 *
 * =============================================================================
 */

import { createContext, useContext } from 'react';

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
